import React, { FC, useMemo, useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@clearcover/atlas';
import { Modal } from 'Shared/components';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import { transformSaveDriversRequest } from 'QuoteApp/services/endpoints/PostSaveDrivers';
import { useAppContext } from 'QuoteApp/services/providers';
import { getDriverDataByFieldValue } from 'QuoteApp/utils';
import { SsnForm } from '../SsnForm';
import styles from './styles.module.css';

type SsnModalPropsType = {
  open: boolean;
  onSubmit(newDetails: GetDetailsResponseType): void;
  onCancel(): void;
};

export const SsnModal: FC<SsnModalPropsType> = ({
  open,
  onCancel,
  onSubmit,
}) => {
  const { details, setDetails, api, config } = useAppContext();
  const [skipValidation, setSkipValidation] = useState(false);
  const submitFormRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (skipValidation && submitFormRef.current) {
      submitFormRef.current();
      setSkipValidation(false);
    }
  }, [skipValidation]);

  const ssnIsOptional = config?.drivers.personalInfo.ssn.is_optional ?? false;

  const driversData = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'value'),
    [details.drivers]
  );

  const driverDataFieldsEditable = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'editable'),
    [details.drivers]
  );

  const driverDataFieldsMasked = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'masked'),
    [details.drivers]
  );

  const driverIdsWithSsnErrors = useMemo(() => {
    const driverErrors = getDriverDataByFieldValue(details.drivers, 'errors');
    return Object.keys(driverErrors).reduce(
      (acc: Array<string>, driverId: string) => {
        if (driverErrors[driverId].ssn?.length) {
          acc.push(driverId);
        }
        return acc;
      },
      []
    );
  }, [details.drivers]);

  const ssnValidationSchema = useMemo(() => {
    if (skipValidation) {
      return {};
    }
    return driverIdsWithSsnErrors.reduce(
      (
        acc: Record<
          string,
          Yup.ObjectSchema<Yup.Shape<object, { ssn: string }>>
        >,
        driverId: string
      ) => {
        acc[driverId] = Yup.object().shape({
          ssn: Yup.string()
            .required('Required')
            .transform((value: string) => value.replace(/[^0-9]/g, ''))
            .length(9, 'Must be a valid SSN'),
        });
        return acc;
      },
      {}
    );
  }, [driverIdsWithSsnErrors, skipValidation]);

  return (
    <Modal showModal={open}>
      <div className={styles.modalContainer}>
        <Formik
          initialValues={{
            drivers: driversData,
          }}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            return api
              .saveAppData({
                data: {
                  values: values.drivers,
                  initialValues: driversData,
                  editableFields: driverDataFieldsEditable,
                  maskedFields: driverDataFieldsMasked,
                },
                transformRequest: transformSaveDriversRequest,
              })
              .then((response) => {
                setDetails(response.data);
                onSubmit(response.data);
              })
              .catch(() => {
                setSubmitting(false);
                onCancel();
                resetForm();
              })
              .finally(() => {
                resetForm();
              });
          }}
          validationSchema={Yup.object().shape({
            drivers: Yup.object().shape(ssnValidationSchema),
          })}
        >
          {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
            submitFormRef.current = handleSubmit;
            return (
              <>
                <h1 className={styles.header}>
                  {ssnIsOptional
                    ? 'Looking to get the right rate for the applicant?'
                    : 'Credit Report'}
                </h1>
                <p className={styles.content}>
                  {ssnIsOptional
                    ? `We can optionally use the applicant's Social Security Number
                      to confirm their information through trusted third-party services
                      and ensure accurate policy pricing. This process does not impact
                      the applicant's credit score. All data is secure and kept confidential.`
                    : `We need a Social Security Number to complete underwriting for
                      this individual. We use SSN to obtain a credit-based insurance
                      score from a third party that is based on a credit report.
                      This will not impact the applicant's credit score, and all
                      data is secure and confidential.`}
                </p>
                <SsnForm driverIdsWithSsnErrors={driverIdsWithSsnErrors} />
                <div className={styles.footer}>
                  <Button
                    onClick={() => {
                      if (ssnIsOptional) {
                        Object.keys(values.drivers).forEach((driverId) => {
                          setFieldValue(`drivers.${driverId}.ssn`, '');
                          setFieldValue(
                            `drivers.${driverId}.declinedSsn`,
                            true
                          );
                        });
                        setSkipValidation(true);
                      } else {
                        onCancel();
                      }
                    }}
                    data-testid="cancel-ssn-modal"
                    variant="secondary"
                  >
                    {ssnIsOptional ? 'Continue without SSN' : 'Cancel'}
                  </Button>
                  <Button
                    data-testid="submit-ssn-button"
                    className={styles.button}
                    variant="primary"
                    onClick={() => {
                      setSkipValidation(false);
                      handleSubmit();
                    }}
                    isSubmitting={isSubmitting}
                  >
                    {ssnIsOptional
                      ? 'Continue with SSN'
                      : 'Submit & Run Reports'}
                  </Button>
                </div>
              </>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};
